import Payouts from '@dolfin/business/domains/payouts'
import Statements from '@dolfin/business/domains/statements'
import { DolfinSupabaseDbRpc } from '@dolfin/business/rpc/supabase'
import { NotificationsQuery, NotificationInsert } from '@dolfin/business/custom_types'
import { Database, TablesInsert } from '@dolfin/business/db_types'
import NotificationItem from '@dolfin/ui/components/notifications/notification-item'
import { NotificationWithReferences } from '@dolfin/ui/components/notifications/reference-labels'
import { t } from '@lingui/core/macro'
import { useSupabaseClient } from '@dolfin/hooks/supabase'
import { isThisMonth, isThisWeek, isToday, isYesterday, subDays } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { QueryKeys } from './queryKeys'
import { IssueWithJoins, ISSUES_UNIFIED_SELECT } from './useIssues'
import { useOrgContext } from './state/organization'
import useSupabaseTable from './supabase-table'

export type { NotificationsQuery } from '@dolfin/business/custom_types'

export type DateFilter = 'unread' | '7days' | '30days' | 'all'
export type NotificationTypeFilter = Database['public']['Enums']['notification_type'] | 'all'

export interface NotificationGroup {
  label: string
  notifications: NotificationWithReferences[]
}

interface useNotificationsProps {
  entity_type?: string
  entity_id?: number
  profile_id?: string
  enabled?: boolean
  type?: Database['public']['Enums']['notification_type']
  dateFilter?: DateFilter
  typeFilter?: NotificationTypeFilter
  includeArchived?: boolean
}

function useNotifications({
  enabled = true,
  dateFilter = 'all',
  typeFilter = 'all',
  includeArchived = false,
  ...props
}: useNotificationsProps) {
  const [writingComment, setWritingComment] = useState<number>()
  const selectedOrganizationId = useOrgContext(state => state.id)
  const currentUserId = useOrgContext(state => state.profile?.id)
  const supabase = useSupabaseClient<Database>()
  const queryClient = useQueryClient()

  const [permissionsGranted, setPermissionsGranted] = useState(false)

  // Calculate date filter range
  const getDateFilterValue = useCallback(() => {
    switch (dateFilter) {
      case '7days':
        return subDays(new Date(), 7).toISOString()
      case '30days':
        return subDays(new Date(), 30).toISOString()
      default:
        return undefined
    }
  }, [dateFilter])

  const { items: notifications, fetchItems: fetchNotifications } = useSupabaseTable<
    NotificationsQuery,
    TablesInsert<'notifications'>
  >({
    queryKeys: [
      QueryKeys.notifications.get,
      props?.entity_id,
      props?.entity_type,
      props?.profile_id,
      dateFilter,
      typeFilter
    ],
    enabled: enabled,
    tableName: 'notifications',
    selectQuery:
      '*, notifications_status!inner(*), profiles!notifications_profile_id_fkey(*), comments(*, profiles!comments_profile_id_fkey(*), comments_mentions(*, profiles(*)))',
    eqFilters: [
      { column: 'organization_id', value: selectedOrganizationId! },
      { column: 'notifications_status.profile_id', value: currentUserId! },
      ...(props?.profile_id ? [{ column: 'profile_id', value: props.profile_id }] : []),
      ...(props?.type ? [{ column: 'type', value: props.type }] : []),
      ...(typeFilter !== 'all' ? [{ column: 'type', value: typeFilter }] : []),
      ...(props?.entity_id && props?.entity_type
        ? [
            { column: 'entity_id', value: props.entity_id },
            { column: 'entity_type', value: props.entity_type }
          ]
        : []),
      // Filter by unread status when dateFilter is 'unread'
      ...(dateFilter === 'unread'
        ? [{ column: 'notifications_status.status', value: 'unread' }]
        : [])
    ],
    // Hide archived notifications unless explicitly included (e.g. entity/comment views)
    neqFilters: includeArchived
      ? undefined
      : [{ column: 'notifications_status.status', value: 'archived' }],
    // Apply date range filter for 7days/30days
    gtFilters: getDateFilterValue()
      ? [{ column: 'created_at', value: getDateFilterValue()!, orEqual: true }]
      : undefined,
    order: [
      {
        column: 'created_at',
        options: {
          ascending: false
        }
      }
    ]
  })

  const issueIds = useMemo(
    () =>
      Array.from(
        new Set(
          (notifications ?? [])
            .filter(
              notification => notification.entity_type === 'issue' && notification.entity_id != null
            )
            .map(notification => notification.entity_id as number)
        )
      ),
    [notifications]
  )

  const { data: issueReferences = [] } = useQuery(
    [QueryKeys.notifications.get, 'references', 'issues', selectedOrganizationId, issueIds],
    async () => {
      const { data, error } = await supabase
        .from('issues')
        .select(ISSUES_UNIFIED_SELECT)
        .eq('organization_id', selectedOrganizationId!)
        .in('id', issueIds)

      if (error) {
        throw error
      }

      return (data ?? []) as IssueWithJoins[]
    },
    {
      enabled: !!selectedOrganizationId && issueIds.length > 0
    }
  )

  const payoutReferenceIds = useMemo(() => {
    const ids = new Set<number>()

    notifications?.forEach(notification => {
      if (notification.entity_type === 'payout' && notification.entity_id != null) {
        ids.add(notification.entity_id as number)
      }
    })

    issueReferences.forEach(issue => {
      if (issue.payout_id != null) {
        ids.add(issue.payout_id)
      }
    })

    return Array.from(ids)
  }, [issueReferences, notifications])

  const statementReferenceIds = useMemo(() => {
    const ids = new Set<number>()

    notifications?.forEach(notification => {
      if (notification.entity_type === 'statement' && notification.entity_id != null) {
        ids.add(notification.entity_id as number)
      }
    })

    issueReferences.forEach(issue => {
      if (issue.statement_id != null) {
        ids.add(issue.statement_id)
      }
    })

    return Array.from(ids)
  }, [issueReferences, notifications])

  const rpcClient = useMemo(() => new DolfinSupabaseDbRpc(supabase), [supabase])

  const { data: payoutReferences = [] } = useQuery(
    [QueryKeys.notifications.get, 'references', 'payouts', payoutReferenceIds],
    () => Payouts.getNotificationReferences(rpcClient, payoutReferenceIds),
    {
      enabled: payoutReferenceIds.length > 0
    }
  )

  const { data: statementReferences = [] } = useQuery(
    [QueryKeys.notifications.get, 'references', 'statements', statementReferenceIds],
    () =>
      Statements.getNotificationReferences({
        rpcClient,
        statementIds: statementReferenceIds
      }),
    {
      enabled: statementReferenceIds.length > 0
    }
  )

  const enrichedNotifications = useMemo<NotificationWithReferences[]>(() => {
    const issuesById = new Map(issueReferences.map(issue => [issue.id, issue]))
    const payoutsById = new Map(payoutReferences.map(reference => [reference.id, reference]))
    const statementsById = new Map(statementReferences.map(reference => [reference.id, reference]))

    return (notifications ?? []).map(notification => {
      const issueReference =
        notification.entity_type === 'issue' && notification.entity_id != null
          ? (issuesById.get(notification.entity_id as number) ?? null)
          : null

      const payoutReference =
        notification.entity_type === 'payout' && notification.entity_id != null
          ? (payoutsById.get(notification.entity_id as number) ?? null)
          : issueReference?.payout_id != null
            ? (payoutsById.get(issueReference.payout_id) ?? null)
            : null

      const statementReference =
        notification.entity_type === 'statement' && notification.entity_id != null
          ? (statementsById.get(notification.entity_id as number) ?? null)
          : issueReference?.statement_id != null
            ? (statementsById.get(issueReference.statement_id) ?? null)
            : null

      return {
        ...notification,
        issue_reference: issueReference,
        payout_reference: payoutReference,
        statement_reference: statementReference
      }
    })
  }, [issueReferences, notifications, payoutReferences, statementReferences])

  useEffect(() => {
    if (enabled) {
      const askForPermission = async () => {
        const permission = window.Notification ? await Notification.requestPermission() : undefined
        setPermissionsGranted(permission === 'granted')
      }

      askForPermission()
    }
  }, [enabled, selectedOrganizationId])

  useEffect(() => {
    if (notifications) {
      const channels = ['notifications_status', 'comments'].map(table =>
        supabase
          .channel(table)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table
            },
            n => {
              fetchNotifications().then(data => {
                const notification = (data.items as unknown as NotificationsQuery[])?.find(
                  nn => nn.id === n.new.notification_id
                )
                if (notification && permissionsGranted) {
                  const userName =
                    n.table === 'comments'
                      ? notification.comments?.find(c => c.id === n.new.id)?.profiles?.name
                      : notification.profiles?.name

                  if (Notification) {
                    new Notification(t`New notification from ${userName ?? 'Someone'}`)
                  }
                }
                if (notification) {
                  queryClient.invalidateQueries(QueryKeys.notifications.get)
                }
              })
            }
          )
          .subscribe()
      )

      return () => {
        channels.forEach(c => c.unsubscribe())
      }
    }
  }, [fetchNotifications, notifications, permissionsGranted, queryClient, supabase])

  const unreadNotifications = enrichedNotifications?.filter(n =>
    n.notifications_status?.find(ns => ns.status === 'unread')
  ).length

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!currentUserId || !enrichedNotifications?.length) return

    const unreadIds = enrichedNotifications
      .filter(n => n.notifications_status?.find(ns => ns.status === 'unread'))
      .map(n => n.id)

    if (!unreadIds.length) return

    const { error } = await supabase
      .from('notifications_status')
      .update({ status: 'read' })
      .eq('profile_id', currentUserId)
      .in('notification_id', unreadIds)

    if (!error) {
      await queryClient.invalidateQueries(QueryKeys.notifications.get)
    }
  }, [currentUserId, enrichedNotifications, supabase, queryClient])

  // Archive a single notification
  const archiveNotification = useCallback(
    async (notificationId: number) => {
      if (!currentUserId) return

      const { error } = await supabase
        .from('notifications_status')
        .update({ status: 'archived' })
        .eq('notification_id', notificationId)
        .eq('profile_id', currentUserId)

      if (!error) {
        await queryClient.invalidateQueries(QueryKeys.notifications.get)
      }
    },
    [currentUserId, supabase, queryClient]
  )

  // Group notifications by date
  const groupNotificationsByDate = useCallback(
    (notificationsList: NotificationWithReferences[]): NotificationGroup[] => {
      if (!notificationsList?.length) return []

      const groups: Record<string, NotificationWithReferences[]> = {
        today: [],
        yesterday: [],
        thisWeek: [],
        thisMonth: [],
        older: []
      }

      notificationsList.forEach(notification => {
        const date = new Date(notification.created_at)
        if (isToday(date)) {
          groups.today.push(notification)
        } else if (isYesterday(date)) {
          groups.yesterday.push(notification)
        } else if (isThisWeek(date)) {
          groups.thisWeek.push(notification)
        } else if (isThisMonth(date)) {
          groups.thisMonth.push(notification)
        } else {
          groups.older.push(notification)
        }
      })

      const result: NotificationGroup[] = []

      if (groups.today.length) {
        result.push({ label: t`Today`, notifications: groups.today })
      }
      if (groups.yesterday.length) {
        result.push({ label: t`Yesterday`, notifications: groups.yesterday })
      }
      if (groups.thisWeek.length) {
        result.push({ label: t`This week`, notifications: groups.thisWeek })
      }
      if (groups.thisMonth.length) {
        result.push({ label: t`This month`, notifications: groups.thisMonth })
      }
      if (groups.older.length) {
        result.push({ label: t`Older`, notifications: groups.older })
      }

      return result
    },
    []
  )

  // Get grouped notifications
  const groupedNotifications = groupNotificationsByDate(enrichedNotifications)

  const insertNotification = async ({
    comment,
    mentions,
    type,
    entity_type,
    entity_id
  }: {
    comment?: string
    mentions?: string[]
  } & TablesInsert<'notifications'>) => {
    const notificationToInsert = {
      type,
      entity_type,
      entity_id,
      organization_id: selectedOrganizationId,
      profile_id: currentUserId
    } as NotificationInsert

    const { data: notification } = await supabase
      .from('notifications')
      .insert(notificationToInsert)
      .select('id')
      .limit(1)
      .single()

    if (notification && comment) {
      const commentToInsert = {
        notification_id: notification.id,
        content: comment,
        profile_id: currentUserId!
      }

      const { data: insertedComment } = await supabase
        .from('comments')
        .insert(commentToInsert)
        .select('id')
        .limit(1)
        .single()

      if (insertedComment && mentions) {
        const mentionsToInsert = mentions.map(m => ({
          comment_id: insertedComment.id,
          profile_id: m
        }))

        await supabase.from('comments_mentions').insert(mentionsToInsert)
      }
    }
  }

  return {
    notifications: enrichedNotifications,
    groupedNotifications,
    writingComment,
    setWritingComment,
    NotificationItem,
    unreadNotifications,
    insertNotification,
    markAllAsRead,
    archiveNotification,
    fetchNotifications
  }
}

export default useNotifications
